import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../services/api';
import KaTeXContent from '../common/KaTeXContent';
import NotesEditorModal from './NotesEditorModal';
import QuizFlashcards from '../common/QuizFlashcards';
import QuizFlashcardEditorModal from '../common/QuizFlashcardEditorModal';
import { formatGradeSectionName, truncateTaskTitle } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';

const buildSuggestedNotesTitle = (notes) => {
  const maxTopicsForTitle = 3;
  const noteTopics = Array.isArray(notes?.topics)
    ? notes.topics
        .map((topic) => (typeof topic === 'string' ? topic.trim() : ''))
        .filter(Boolean)
    : [];

  const uniqueTopics = [...new Set(noteTopics)];
  if (uniqueTopics.length > 0 && uniqueTopics.length <= maxTopicsForTitle) {
    return truncateTaskTitle(uniqueTopics.join(', '), 30, { fallback: '', suffix: '…' });
  }

  const typeLabel = notes.type === 'quiz' ? 'Quiz' : `${notes.type} Notes`;
  return truncateTaskTitle(`${notes.subject} - ${typeLabel}`, 30, { fallback: '', suffix: '…' });
};

const NotesAssignmentView = ({ notes, onAssign, onPrint, onDownload, onGenerateNew, onSaveClose, target }) => {
  const { user } = useAuth();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const suggestedAssignmentTitle = useMemo(() => buildSuggestedNotesTitle(notes), [notes]);
  const [assignmentData, setAssignmentData] = useState({
    title: suggestedAssignmentTitle,
    description: '',
    target_type: 'child',
    target_ids: []
  });
  const [assignmentTargets, setAssignmentTargets] = useState({ students: [], batches: [], children: [] });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(user?.role || null);
  const [isEditing, setIsEditing] = useState(false);
  const [notesContent, setNotesContent] = useState(notes.html_content);
  const [saveLoading, setSaveLoading] = useState(false);
  const [flashcards, setFlashcards] = useState(Array.isArray(notes.flashcards) ? notes.flashcards : []);
  const [isEditingFlashcards, setIsEditingFlashcards] = useState(false);
  const isFlashcardOnly = notes.type === 'quiz';
  // Show the quiz right away — the creator should see what got generated
  // without an extra click, same as they'd see the notes body immediately.
  const [showFlashcards, setShowFlashcards] = useState(true);

  const filteredBatches = useMemo(() => {
    const normalizedSubject = (notes.subject || '').toLowerCase();

    if (!normalizedSubject) return assignmentTargets.batches || [];

    return (assignmentTargets.batches || []).filter((batch) => {
      const batchSubject = (batch.subject || '').toLowerCase();
      return batchSubject === normalizedSubject;
    });
  }, [assignmentTargets.batches, notes.subject]);

  const targetBatchId = useMemo(() => {
    const rawTargetId = target?.id;
    if (!rawTargetId) return null;

    if (typeof rawTargetId === 'string' && rawTargetId.startsWith('batch_')) {
      const parsedId = Number.parseInt(rawTargetId.replace('batch_', ''), 10);
      return Number.isNaN(parsedId) ? null : parsedId;
    }

    const matchedBatch = (assignmentTargets.batches || []).find(
      (batch) => String(batch.id) === String(rawTargetId)
    );

    return matchedBatch?.id ?? null;
  }, [assignmentTargets.batches, target?.id]);


  const targetStudentBatchIds = useMemo(() => {
    if (!target?.id) return [];

    const targetStudent = (assignmentTargets.students || []).find(
      (student) => String(student.id) === String(target.id)
    );

    if (!targetStudent) {
      const directBatchIds = Array.isArray(target?.batch_ids)
        ? target.batch_ids
        : target?.batch_id !== undefined && target?.batch_id !== null
          ? [target.batch_id]
          : [];
      return directBatchIds;
    }

    if (Array.isArray(targetStudent.batch_ids) && targetStudent.batch_ids.length > 0) {
      return targetStudent.batch_ids;
    }

    if (targetStudent.batch_id !== undefined && targetStudent.batch_id !== null) {
      return [targetStudent.batch_id];
    }

    return [];
  }, [assignmentTargets.students, target?.id, target?.batch_id, target?.batch_ids]);

  const filteredStudents = useMemo(() => {
    const selectedBatchIds = selectedBatches.length > 0
      ? selectedBatches
      : targetBatchId
        ? [targetBatchId]
        : targetStudentBatchIds;

    if (selectedBatchIds.length === 0) {
      return [];
    }

    const normalizedBatchIds = new Set(selectedBatchIds.map((id) => String(id)));

    return (assignmentTargets.students || []).filter((student) => {
      const studentBatchIds = Array.isArray(student.batch_ids)
        ? student.batch_ids.map((id) => String(id))
        : student.batch_id !== undefined && student.batch_id !== null
          ? [String(student.batch_id)]
          : [];

      if (studentBatchIds.length === 0) {
        return false;
      }

      return studentBatchIds.some((batchId) => normalizedBatchIds.has(batchId));
    });
  }, [assignmentTargets.students, selectedBatches, targetBatchId, targetStudentBatchIds]);

  useEffect(() => {
    loadAvailableTargets();
  }, []);

  useEffect(() => {
    setNotesContent(notes.html_content);
  }, [notes.html_content]);

  useEffect(() => {
    setFlashcards(Array.isArray(notes.flashcards) ? notes.flashcards : []);
  }, [notes.flashcards]);

  // Pre-select default recipients based on context
  useEffect(() => {
    if (!userRole) return;

    const hasSelectedTargets = selectedStudents.length > 0 || selectedBatches.length > 0;

    if (['coaching_teacher', 'school_teacher'].includes(userRole)) {
      if (targetBatchId) {
        if (selectedBatches.some((batchId) => String(batchId) === String(targetBatchId))) {
          return;
        }

        if (filteredBatches.some((batch) => String(batch.id) === String(targetBatchId))) {
          setAssignmentData(prev => ({ ...prev, target_type: 'batch' }));
          setSelectedBatches([targetBatchId]);
          return;
        }

        const targetBatchInAllBatches = (assignmentTargets.batches || []).find(
          (batch) => String(batch.id) === String(targetBatchId)
        );

        if (targetBatchInAllBatches) {
          setAssignmentData(prev => ({ ...prev, target_type: 'batch' }));
          setSelectedBatches([targetBatchInAllBatches.id]);
          return;
        }
      }

      if (hasSelectedTargets) return;

      const targetStudentBatchInSubject = filteredBatches.find((batch) =>
        targetStudentBatchIds.some((batchId) => String(batch.id) === String(batchId))
      );

      if (targetStudentBatchInSubject) {
        setAssignmentData(prev => ({ ...prev, target_type: 'child' }));
        setSelectedBatches([targetStudentBatchInSubject.id]);
        if (target?.id) {
          setSelectedStudents([target.id]);
        }
        return;
      }

      if (filteredBatches.length > 0) {
        setAssignmentData(prev => ({ ...prev, target_type: 'batch' }));
        setSelectedBatches([filteredBatches[0].id]);
        return;
      }

      if (target?.id && !targetBatchId) {
        setAssignmentData(prev => ({ ...prev, target_type: 'child' }));
        setSelectedStudents([target.id]);
        return;
      }
    } else {
      if (hasSelectedTargets) return;

      if (target?.id) {
        setSelectedStudents([target.id]);
        return;
      }

      if (assignmentTargets.children?.length > 0) {
        setSelectedStudents([assignmentTargets.children[0].id]);
      }
    }
  }, [userRole, target?.id, targetBatchId, assignmentTargets, filteredBatches, targetStudentBatchIds, selectedStudents.length, selectedBatches.length]);

  useEffect(() => {
    if (userRole === 'parent' || assignmentData.target_type !== 'batch') return;

    const validBatchIds = selectedBatches.filter((batchId) => {
      const matchesFilteredBatch = filteredBatches.some((batch) => batch.id === batchId);
      const matchesTargetBatch = targetBatchId && String(batchId) === String(targetBatchId);
      return matchesFilteredBatch || matchesTargetBatch;
    });

    if (validBatchIds.length !== selectedBatches.length) {
      if (validBatchIds.length > 0) {
        setSelectedBatches(validBatchIds);
      } else if (targetBatchId) {
        setSelectedBatches([targetBatchId]);
      } else if (filteredBatches.length > 0) {
        setSelectedBatches([filteredBatches[0].id]);
      } else {
        setSelectedBatches([]);
      }
    } else if (validBatchIds.length === 0) {
      if (targetBatchId) {
        setSelectedBatches([targetBatchId]);
      } else if (filteredBatches.length > 0) {
        setSelectedBatches([filteredBatches[0].id]);
      }
    }
  }, [userRole, assignmentData.target_type, filteredBatches, selectedBatches, targetBatchId]);

  useEffect(() => {
    if (userRole === 'parent' || assignmentData.target_type !== 'child') return;

    const validStudentIds = new Set(filteredStudents.map((student) => student.id));
    const nextSelectedStudents = selectedStudents.filter((studentId) => validStudentIds.has(studentId));

    if (nextSelectedStudents.length !== selectedStudents.length) {
      setSelectedStudents(nextSelectedStudents);
    }
  }, [userRole, assignmentData.target_type, filteredStudents, selectedStudents]);

  const loadAvailableTargets = async () => {
    try {
      // Use role from AuthContext — avoids an /auth/me round-trip that could
      // race with an in-flight generation request and trigger a spurious logout.
      const role = user?.role || userRole;
      if (role) {
        setUserRole(role);
      }

      if (['coaching_teacher', 'school_teacher'].includes(role)) {
        setAssignmentData(prev => ({ ...prev, target_type: 'batch' }));
        // Load assignment targets (batches and students) for coaching teacher
        const response = await axios.get('/coaching/assignment-targets');
        setAssignmentTargets(response.data);
      } else {
        // Load children for parent
        const childrenResponse = await axios.get('/children');
        if (childrenResponse.data.children) {
          setAssignmentTargets({
            students: [],
            batches: [],
            children: childrenResponse.data.children
          });
        }
      }
    } catch (error) {
      console.error('Error loading targets:', error);
    }
  };

  const handleAssign = async () => {
    if (loading) return;
    // Validation
    if (['coaching_teacher', 'school_teacher'].includes(userRole)) {
      if (assignmentData.target_type === 'child' && selectedStudents.length === 0) {
        alert('Please select at least one student');
        return;
      }
      if (assignmentData.target_type === 'batch' && selectedBatches.length === 0) {
        alert('Please select at least one batch');
        return;
      }
    } else {
      // Parent validation
      if (selectedStudents.length === 0) {
        alert('Please select at least one child');
        return;
      }
    }
    
    setLoading(true);
    try {
      // Prepare assignment data with correct target_ids
      const finalAssignmentData = {
        ...assignmentData,
        target_ids: assignmentData.target_type === 'child' ? selectedStudents : selectedBatches
      };
      
      await onAssign(finalAssignmentData);
      setShowAssignModal(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (htmlContent) => {
    setSaveLoading(true);
    try {
      await axios.put(`/notes/${notes.id}`, {
        html_content: htmlContent
      });
      
      notes.html_content = htmlContent;
      setNotesContent(htmlContent);
      alert('Notes updated successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveFlashcards = async (questions) => {
    try {
      const response = await axios.put(`/notes/${notes.id}`, {
        flashcards: questions,
      });
      const saved = Array.isArray(response.data?.flashcards) ? response.data.flashcards : questions;
      setFlashcards(saved);
      notes.flashcards = saved;
      setIsEditingFlashcards(false);
      alert('Quiz updated successfully');
    } catch (error) {
      console.error('Error saving quiz:', error);
      alert('Failed to save quiz');
      throw error;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{isFlashcardOnly ? '🎴 Quiz Ready!' : 'Generated Notes'}</h3>
      </div>
      
      {!isEditing ? (
        <>
          {!isFlashcardOnly && (
            <KaTeXContent
              content={notesContent}
              className="prose max-w-none border rounded-lg p-4 bg-gray-50 overflow-auto max-h-96"
            />
          )}

          {flashcards.length > 0 && (
            <div className={isFlashcardOnly ? '' : 'border rounded-lg p-4 bg-white'}>
              {!isFlashcardOnly && (
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                    <span>🎴</span>
                    <span>Quiz ({flashcards.length} cards)</span>
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowFlashcards((prev) => !prev)}
                    className="text-xs font-semibold text-purple-600 hover:text-purple-800"
                  >
                    {showFlashcards ? 'Hide Quiz' : 'Show Quiz'}
                  </button>
                </div>
              )}
              {showFlashcards && (
                <QuizFlashcards questions={flashcards} previewMode />
              )}
            </div>
          )}

          <div className="sticky bottom-0 z-0 flex flex-col gap-3 border-t border-gray-200 bg-white/95 pb-4 pt-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                onClick={onGenerateNew}
                className="py-2 px-4 text-sm border bg-gray-100 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-200"
              >
                Back
              </button>
              {onSaveClose && (
                <button
                  onClick={onSaveClose}
                  title="Save — you can assign it later from Your notes"
                  className="py-2 px-4 text-sm bg-orange-100 rounded-lg text-gray-700 hover:bg-orange-200 font-semibold"
                >
                  Save
                </button>
              )}
              {flashcards.length > 0 && (
                <button
                  onClick={() => setIsEditingFlashcards(true)}
                  className="py-2 px-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Edit Quiz
                </button>
              )}
              {!isFlashcardOnly && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="py-2 px-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Edit Notes
                  </button>
                  <button
                    onClick={onPrint}
                    className="py-2 px-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Print
                  </button>
                  <button
                    onClick={onDownload}
                    className="py-2 px-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Download
                  </button>
                </>
              )}
              <button
                onClick={() => setShowAssignModal(true)}
                className="py-2 px-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Assign
              </button>
            </div>
          </div>

          {isEditingFlashcards && (
            <QuizFlashcardEditorModal
              initialQuestions={flashcards}
              title="Edit quiz"
              onClose={() => setIsEditingFlashcards(false)}
              onSave={handleSaveFlashcards}
            />
          )}
        </>
      ) : (
      <NotesEditorModal
        isOpen={isEditing}
        initialContent={notesContent}
        isSaving={saveLoading}
        onClose={() => setIsEditing(false)}
        onSave={async (htmlContent) => {
          await handleSaveEdit(htmlContent);
          setIsEditing(false);
        }}
      />
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs uppercase text-purple-100 tracking-wide">Assign Task</p>
                  <h4 className="text-xl font-semibold text-white">Send Notes to Students</h4>
                  <p className="text-sm text-purple-100">We preselect recipients from your current context.</p>
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold">📝</div>
                  <div>
                    <p className="text-sm text-gray-600">Customize the title and instructions before sending.</p>
                    <p className="text-xs text-gray-400">Recipients can be adjusted below.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Title *</label>
                  <input
                    type="text"
                    value={assignmentData.title}
                    onChange={(e) => setAssignmentData({...assignmentData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 bg-gray-50"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Description (Optional)</label>
                  <textarea
                    value={assignmentData.description}
                    onChange={(e) => setAssignmentData({...assignmentData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 bg-gray-50"
                    rows="3"
                    placeholder="Add instructions for students..."
                  />
                </div>
              </div>

              {['coaching_teacher', 'school_teacher'].includes(userRole) && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">Assign To</label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`flex items-center space-x-2 p-3 rounded-lg border ${assignmentData.target_type === 'child' ? 'border-indigo-200 bg-indigo-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                      <input
                        type="radio"
                        name="target_type"
                        value="child"
                        checked={assignmentData.target_type === 'child'}
                        onChange={(e) => setAssignmentData({...assignmentData, target_type: e.target.value})}
                        className="text-indigo-600"
                      />
                      <span className="text-sm text-gray-800">Individual Students</span>
                    </label>
                    <label className={`flex items-center space-x-2 p-3 rounded-lg border ${assignmentData.target_type === 'batch' ? 'border-indigo-200 bg-indigo-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                      <input
                        type="radio"
                        name="target_type"
                        value="batch"
                        checked={assignmentData.target_type === 'batch'}
                        onChange={(e) => setAssignmentData({...assignmentData, target_type: e.target.value})}
                        className="text-indigo-600"
                      />
                      <span className="text-sm text-gray-800">Batches</span>
                    </label>
                  </div>
                </div>
              )}

              {['coaching_teacher', 'school_teacher'].includes(userRole) && assignmentData.target_type === 'child' && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Students</label>
                    <span className="text-xs text-gray-500">Multi-select allowed</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {filteredStudents.map((student) => (
                      <label key={student.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStudents([...selectedStudents, student.id]);
                            } else {
                              setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800">{student.name} (Grade {student.grade})</span>
                      </label>
                    ))}
                    {filteredStudents.length === 0 && (
                      <div className="text-sm text-gray-500 px-2 py-1">
                        There are no students in the batch.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {['coaching_teacher', 'school_teacher'].includes(userRole) && assignmentData.target_type === 'batch' && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Batches</label>
                    <span className="text-xs text-gray-500">Class-wide sharing</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50 space-y-1">
                    {filteredBatches.length === 0 && (
                      <div className="text-sm text-gray-500 px-2 py-1">
                        No batches available for {notes.subject}. Update the subject or create a matching batch.
                      </div>
                    )}
                    {filteredBatches.map((batch) => (
                      <label key={batch.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                        <input
                          type="checkbox"
                          checked={selectedBatches.includes(batch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBatches([...selectedBatches, batch.id]);
                            } else {
                              setSelectedBatches(selectedBatches.filter(id => id !== batch.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800">
                          {userRole === 'school_teacher'
                            ? `${formatGradeSectionName(batch.name, batch.grade)} - ${batch.subject}`
                            : `${batch.name} - ${batch.subject} (Grade ${batch.grade})`}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {!['coaching_teacher', 'school_teacher'].includes(userRole) && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Children</label>
                    <span className="text-xs text-gray-500">Tap to include/exclude</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {assignmentTargets.children?.map((child) => (
                      <label key={child.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(child.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStudents([...selectedStudents, child.id]);
                            } else {
                              setSelectedStudents(selectedStudents.filter(id => id !== child.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800">{child.name} (Grade {child.grade})</span>
                      </label>
                    ))}
                  </div>
                  {selectedStudents.length > 0 && (
                    <p className="text-xs text-green-600 mt-2">
                      {selectedStudents.length} child(ren) selected
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center space-x-2 pt-2">
                <p className="text-xs text-gray-500">Students will receive notes instantly in their workspace.</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowAssignModal(false)}
                    className="py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={loading}
                    className="py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 shadow"
                  >
                    {loading ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesAssignmentView;